import { useAuth0 } from "@auth0/auth0-react";
import { useCallback } from "react";

const API_BASE_URL = process.env.REACT_APP_FINDER_API_BASE_URL;

const isBasicAuth =
  process.env.REACT_APP_FINDER_API_USE_BASIC_AUTH === "true" || false;
const encodedCredentials = isBasicAuth
  ? btoa(
      `${process.env.REACT_APP_FINDER_API_BASIC_AUTH_USER}:${process.env.REACT_APP_FINDER_API_BASIC_AUTH_PASS}`
    )
  : null;

export const useFinderApi = () => {
  const { getAccessTokenSilently } = useAuth0();

  const handleResponse = async (response) => {
    if (!response.ok) {
      const error = await response.json();
      if(error?.error_code) {
        throw error;
      }
      throw new Error(error.message || 'Something went wrong');
    }
    return response.json();
  };

  const getOpenNeeds = useCallback(async () => {
    let authString;
    if (isBasicAuth) {
      authString = `Basic ${encodedCredentials}`;
    } else {
      const token = await getAccessTokenSilently();
      authString = `Bearer ${token}`;
    }
    const headers = {
      "Content-Type": "application/json",
      Authorization: `${authString}`,
    };

    const response = await fetch(`${API_BASE_URL}/needs?status=open`, {
      headers: headers,
    });
    return handleResponse(response);
  }, [getAccessTokenSilently]);

  const getReservedNeeds = useCallback(async () => {
    let authString;
    if (isBasicAuth) {
      authString = `Basic ${encodedCredentials}`;
    } else {
      const token = await getAccessTokenSilently();
      authString = `Bearer ${token}`;
    }
    const headers = {
      "Content-Type": "application/json",
      Authorization: `${authString}`,
    };

    const response = await fetch(`${API_BASE_URL}/needs?status=reserved`, {
      headers: headers,
    });
    return handleResponse(response);
  }, [getAccessTokenSilently]);

  const getNeedById = useCallback(
    async (id) => {
      let authString;
      if (isBasicAuth) {
        authString = `Basic ${encodedCredentials}`;
      } else {
        const token = await getAccessTokenSilently();
        authString = `Bearer ${token}`;
      }
      const headers = {
        "Content-Type": "application/json",
        Authorization: `${authString}`,
      };

      const response = await fetch(`${API_BASE_URL}/needs/${id}`, {
        headers: headers,
      });
      return handleResponse(response);
    },
    [getAccessTokenSilently]
  );

  const createNeed = useCallback(
    async (needData) => {
      let authString;
      if (isBasicAuth) {
        authString = `Basic ${encodedCredentials}`;
      } else {
        const token = await getAccessTokenSilently();
        authString = `Bearer ${token}`;
      }
      const headers = {
        "Content-Type": "application/json",
        Authorization: `${authString}`,
      };

      const response = await fetch(`${API_BASE_URL}/needs`, {
        method: "POST",
        headers: headers,
        body: JSON.stringify(needData),
      });
      return handleResponse(response);
    },
    [getAccessTokenSilently]
  );

  const updateNeedForId = useCallback(
    async (id, needData) => {
      let authString;
      if (isBasicAuth) {
        authString = `Basic ${encodedCredentials}`;
      } else {
        const token = await getAccessTokenSilently();
        authString = `Bearer ${token}`;
      }
      const headers = {
        "Content-Type": "application/json",
        Authorization: `${authString}`,
      };

      const response = await fetch(`${API_BASE_URL}/needs/${id}`, {
        method: "PUT",
        headers: headers,
        body: JSON.stringify(needData),
      });
      return handleResponse(response);
    },
    [getAccessTokenSilently]
  );

  const searchSpaces = useCallback(
    async (id) => {
      let authString;
      if (isBasicAuth) {
        authString = `Basic ${encodedCredentials}`;
      } else {
        const token = await getAccessTokenSilently();
        authString = `Bearer ${token}`;
      }
      const headers = {
        "Content-Type": "application/json",
        Authorization: `${authString}`,
      };

      const response = await fetch(`${API_BASE_URL}/needs/${id}/search`, {
        method: "POST",
        headers: headers,
      });
      return handleResponse(response);
    },
    [getAccessTokenSilently]
  );

  const makeReservation = useCallback(
    async (id, reservationData) => {
      let authString;
      if (isBasicAuth) {
        authString = `Basic ${encodedCredentials}`;
      } else {
        const token = await getAccessTokenSilently();
        authString = `Bearer ${token}`;
      }
      const headers = {
        "Content-Type": "application/json",
        Authorization: `${authString}`,
      };

      const response = await fetch(`${API_BASE_URL}/needs/${id}/reservation`, {
        method: "POST",
        headers: headers,
        body: JSON.stringify(reservationData),
      });
      return handleResponse(response);
    },
    [getAccessTokenSilently]
  );

  const cancelReservation = useCallback(
    async (id, cancellationReason , otherReason ) => {
      let authString;
      if (isBasicAuth) {
        authString = `Basic ${encodedCredentials}`;
      } else {
        const token = await getAccessTokenSilently();
        authString = `Bearer ${token}`;
      }
      const headers = {
        "Content-Type": "application/json",
        Authorization: `${authString}`,
      };

    const body = {
      cancellation_reason: cancellationReason,
      ...(cancellationReason === "other" && { other_reason: otherReason }),
    };

    const response = await fetch(
      `${API_BASE_URL}/needs/${id}/reservation/cancellation`,
      {
        method: "POST",
        headers,
        body: JSON.stringify(body),
      }
    );
    return handleResponse(response);
  },
  [getAccessTokenSilently]
);

  const getUserDetails = useCallback(async () => {
    let authString;
    if (isBasicAuth) {
      authString = `Basic ${encodedCredentials}`;
    } else {
      const token = await getAccessTokenSilently();
      authString = `Bearer ${token}`;
    }
    const headers = {
      "Content-Type": "application/json",
      Authorization: `${authString}`,
    };

    const response = await fetch(`${API_BASE_URL}/me`, {
      headers: headers,
    });
    return handleResponse(response);
  }, [getAccessTokenSilently]);

  const updateUserDetails = useCallback(
    async (userData) => {
      let authString;
      if (isBasicAuth) {
        authString = `Basic ${encodedCredentials}`;
      } else {
        const token = await getAccessTokenSilently();
        authString = `Bearer ${token}`;
      }
      const headers = {
        "Content-Type": "application/json",
        Authorization: `${authString}`,
      };

      const payload = { phone: userData.phone };

      const response = await fetch(`${API_BASE_URL}/me`, {
        method: "PATCH",
        headers: headers,
        body: JSON.stringify(payload),
      });
      return handleResponse(response);
    },
    [getAccessTokenSilently]
  );

  const getNotificationPreferences = useCallback(async (userId) => {
    let authString;
    if (isBasicAuth) {
      authString = `Basic ${encodedCredentials}`;
    } else {
      const token = await getAccessTokenSilently();
      authString = `Bearer ${token}`;
    }
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `${authString}`,
    };
    const API_BASE_URL = process.env.REACT_APP_FINDER_API_BASE_URL;
    const response = await fetch(`${API_BASE_URL}/me/notification-preferences?user_id=${userId}`, {
      headers: headers,
    });
    if (!response.ok) throw new Error('Failed to fetch notification preferences');
    const data = await response.json();
    return data.preferences;
  }, [getAccessTokenSilently]);

  const updateNotificationPreferences = useCallback(async (userId, preferences) => {
    let authString;
    if (isBasicAuth) {
      authString = `Basic ${encodedCredentials}`;
    } else {
      const token = await getAccessTokenSilently();
      authString = `Bearer ${token}`;
    }
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `${authString}`,
    };
    const API_BASE_URL = process.env.REACT_APP_FINDER_API_BASE_URL;
    const response = await fetch(`${API_BASE_URL}/me/notification-preferences?user_id=${userId}`, {
      method: 'PUT',
      headers: headers,
      body: JSON.stringify(preferences),
    });
    if (!response.ok) throw new Error('Failed to update notification preferences');
    return await response.json();
  }, [getAccessTokenSilently]);

  const getNotificationEnums = useCallback(async () => {
    let authString;
    if (isBasicAuth) {
      authString = `Basic ${encodedCredentials}`;
    } else {
      const token = await getAccessTokenSilently();
      authString = `Bearer ${token}`;
    }
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `${authString}`,
    };
    const response = await fetch(`${API_BASE_URL}/types/notification-preferences`, {
      headers: headers,
    });
    if (!response.ok) throw new Error('Failed to fetch notification enums');
    return await response.json();
  }, [getAccessTokenSilently]);

  // const validatePhoneWithTwilio = useCallback(async (phone) => {
  //   let authString;
  //   if (isBasicAuth) {
  //     authString = `Basic ${encodedCredentials}`;
  //   } else {
  //     const token = await getAccessTokenSilently();
  //     authString = `Bearer ${token}`;
  //   }
  //   const headers = {
  //     'Content-Type': 'application/json',
  //     'Authorization': `${authString}`,
  //   };

  //   const API_BASE_URL = process.env.REACT_APP_FINDER_API_BASE_URL;
  //   const res = await fetch(`${API_BASE_URL}/lookup?phone=${encodeURIComponent(phone)}`, {
  //     headers: headers,
  //   });
  //   if (!res.ok) {
  //     let data = {};
  //     try { data = await res.json(); } catch {}
  //     throw new Error(data.error || "Phone validation failed");
  //   }
  //   return await res.json();
  // }, [getAccessTokenSilently]);

  return {
    getOpenNeeds,
    getReservedNeeds,
    getNeedById,
    createNeed,
    updateNeedForId,
    searchSpaces,
    makeReservation,
    cancelReservation,
    getUserDetails,
    updateUserDetails,
    getNotificationPreferences,
    updateNotificationPreferences,
    // validatePhoneWithTwilio,
    getNotificationEnums,
  };
};
